import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    position: "",
    company: "",
    email: "",
    phone: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit form');
      }

      const result = await response.json();
      
      // Track Meta Pixel conversion
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'Contact', {
          content_name: 'Mining Investment Inquiry',
          content_category: 'Lead Generation'
        });
      }
      
      toast.success(result.message || "Thank you! Your message has been sent. We'll get back to you soon.");
      
      setFormData({
        fullName: "",
        position: "", 
        company: "",
        email: "",
        phone: "",
        message: ""
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || "Something went wrong. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-black to-yellow-900/20">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Contact Info */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-yellow-500 mb-6">
              Talk to us about building your brand or generating leads
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Looking to connect with the mining industry's most engaged audience? 
              Kitco helps you reach decision-makers, investors, and industry leaders who can turn visibility into tangible business opportunities.
            </p>

            {/* Contact Information */}
            <div className="space-y-6 mb-12">
              <div className="flex items-center space-x-4">
                <div className="bg-yellow-500 p-3 rounded-full">
                  <Phone className="w-5 h-5 text-black" />
                </div>
                <div>
                  <div className="text-white font-semibold">Phone</div>
                  <div className="text-gray-300">1 855 585-4826</div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="bg-yellow-500 p-3 rounded-full">
                  <Mail className="w-5 h-5 text-black" />
                </div>
                <div>
                  <div className="text-white font-semibold">Email</div>
                  <div className="text-gray-300">
                    <span data-email="advertising" data-domain="kitco.com" className="email-obfuscated cursor-pointer hover:text-yellow-500 transition-colors">
                      Click to reveal email
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="bg-yellow-500 p-3 rounded-full">
                  <MapPin className="w-5 h-5 text-black" />
                </div>
                <div>
                  <div className="text-white font-semibold">Address</div>
                  <div className="text-gray-300">620 Cathcart Street, Suite 900<br />Montreal, Quebec H3B 1M1</div>
                </div>
              </div>
            </div>

          </div>

          {/* Right Side - Contact Form */}
          <div>
            <Card className="bg-yellow-500 border-0">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-black text-center">
                  Get In Touch
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fullName" className="text-black font-semibold">
                        Full Name *
                      </Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-white border-gray-300 text-black"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="position" className="text-black font-semibold">
                        Position *
                      </Label>
                      <Input
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-white border-gray-300 text-black"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-black font-semibold">
                      Company *
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="mt-1 bg-white border-gray-300 text-black"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-black font-semibold">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-white border-gray-300 text-black"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="text-black font-semibold">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-white border-gray-300 text-black"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-black font-semibold">
                      Tell us more
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="mt-1 bg-white border-gray-300 text-black"
                      placeholder="Tell us about your advertising objectives and how we can help..."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-black hover:bg-gray-800 text-white font-bold py-3 text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;